/**
 * Shared controller for popups built on the levit-popup-block markup:
 *
 *   <div class="levit-popup-container ...">
 *     <button class="popup-close-icon"></button>
 *     <div class="levit-popup-wrapper ...">...</div>
 *   </div>
 *
 * Handles:
 *   - moving the container to <body> so it escapes any stacking-context ancestor
 *   - open/close via `.toggled` class + aria-hidden
 *   - body scroll-lock class
 *   - close on backdrop click, Escape key, and .popup-close-icon click
 *
 * Usage:
 *   const popup = new LevitPopup(container, {
 *     bodyClass: 'build-popup-open',
 *     onOpen: () => {...},
 *     onClose: () => {...},
 *   });
 *   popup.open();
 *   popup.close();
 *   popup.destroy();
 */
class LevitPopup {
	constructor(container, options = {}) {
		this.container = container;
		this.bodyClass = options.bodyClass || '';
		this.onOpenCallback = options.onOpen || null;
		this.onCloseCallback = options.onClose || null;

		this.boundKeydown = this.onKeydown.bind(this);
		this.boundBackdropClick = this.onBackdropClick.bind(this);
		this.boundCloseClick = () => this.close();

		this.init();
	}

	init() {
		if (!this.container) return;

		if (this.container.parentElement !== document.body) {
			document.body.appendChild(this.container);
		}

		this.closeButton = this.container.querySelector('.popup-close-icon');
		this.closeButton?.addEventListener('click', this.boundCloseClick);
		this.container.addEventListener('click', this.boundBackdropClick);
		document.addEventListener('keydown', this.boundKeydown);
	}

	onBackdropClick(event) {
		if (event.target === this.container) this.close();
	}

	onKeydown(event) {
		if (event.key === 'Escape' && this.isOpen()) this.close();
	}

	isOpen() {
		return !!this.container?.classList.contains('toggled');
	}

	open() {
		if (!this.container) return;
		this.container.classList.add('toggled');
		this.container.setAttribute('aria-hidden', 'false');
		if (this.bodyClass) document.body.classList.add(this.bodyClass);
		this.onOpenCallback?.();
	}

	close() {
		if (!this.container) return;
		this.container.classList.remove('toggled');
		this.container.setAttribute('aria-hidden', 'true');
		if (this.bodyClass) document.body.classList.remove(this.bodyClass);
		this.onCloseCallback?.();
	}

	destroy() {
		this.closeButton?.removeEventListener('click', this.boundCloseClick);
		this.container?.removeEventListener('click', this.boundBackdropClick);
		document.removeEventListener('keydown', this.boundKeydown);
	}
}

export default LevitPopup;
